"use client";

import { useEffect, useState, useCallback } from "react";
import { Link } from "@/components/common/Link";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
import { Button } from "@/components/ui/button";
import { WvcLogo } from "@/integrations/wordpress/WvcLogo";
import { ArrowRight, MapPin, ChevronLeft, ChevronRight } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
const destinations = [{
  name: "Bali",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1713427/img/bali_escape.png",
  description: "Spiritual retreats and sun-drenched shores."
}, {
  name: "Thailand",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1713427/img/thailand_adventure.png",
  description: "Emerald waters and vibrant street culture."
}, {
  name: "Vietnam",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1713427/img/vietnam_heritage.png",
  description: "Timeless landscapes and culinary wonders."
}, {
  name: "Switzerland",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1713427/img/switzerland_alps.png",
  description: "Alpine luxury and pristine mountain peaks."
}, {
  name: "Japan",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1713427/img/japan_tradition.png",
  description: "Neon horizons meeting ancient traditions."
}, {
  name: "Maldives",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1713427/img/maldives_luxury.png",
  description: "Overwater villas and turquoise serenity."
}];
export default function Departure() {
  const [imageLoaded, setImageLoaded] = useState(false);
  const [currentSlide, setCurrentSlide] = useState(0);
  const [[page, direction], setPage] = useState([0, 0]);
  const {
    ref: sectionRef,
    isVisible
  } = useRevealOnScroll<HTMLDivElement>({
    once: true,
    threshold: 0.1
  });
  useEffect(() => {
    const timer = setTimeout(() => setImageLoaded(true), 100);
    return () => clearTimeout(timer);
  }, []);
  const paginate = useCallback((newDirection: number) => {
    const nextSlide = (currentSlide + newDirection + destinations.length) % destinations.length;
    setPage([nextSlide, newDirection]);
    setCurrentSlide(nextSlide);
  }, [currentSlide]);
  useEffect(() => {
    const interval = setInterval(() => {
      paginate(1);
    }, 5000);
    return () => clearInterval(interval);
  }, [paginate]);
  const variants = {
    enter: (direction: number) => ({
      x: direction > 0 ? 200 : -200,
      opacity: 0,
      scale: 0.95
    }),
    center: {
      zIndex: 1,
      x: 0,
      opacity: 1,
      scale: 1
    },
    exit: (direction: number) => ({
      zIndex: 0,
      x: direction < 0 ? 200 : -200,
      opacity: 0,
      scale: 0.95
    })
  };
  return <section data-section-id="17721" id="the-art-of-departure" ref={sectionRef} className="relative w-full bg-background overflow-hidden">
      <div className="relative w-full h-[90vh] min-h-[800px] flex items-center justify-center">
        {}
        <div className="absolute inset-0 z-0">
          <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1713427/img/innoleaf_global_landscape.png" data-wvc-srcset="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1713427/img/innoleaf_global_landscape.png-wvc-srcset" data-wvc-sizes="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1713427/img/innoleaf_global_landscape.png-wvc-sizes" alt="Breathtaking panoramic view of a serene landscape" className={`w-full h-full object-cover transition-transform duration-[5000ms] ease-out ${imageLoaded ? "scale-100" : "scale-110"}`} loading="lazy" decoding="async" />
          <div className="absolute inset-0 bg-black/40 backdrop-blur-[2px]" />
        </div>

        {}
        <div className="relative z-10 container mx-auto px-6 text-center">
          <motion.div initial={{
          opacity: 0,
          y: 30
        }} animate={isVisible ? {
          opacity: 1,
          y: 0
        } : {}} transition={{
          duration: 0.8,
          ease: "easeOut"
        }} className="max-w-5xl mx-auto">
            <div className="flex items-center justify-center gap-3 mb-8">
              <div className="h-px w-12 bg-primary" />
              <span className="font-sans text-sm uppercase tracking-[0.3em] text-primary-foreground/90">
                The Art of Departure
              </span>
              <div className="h-px w-12 bg-primary" />
            </div>

            {}
            <div className="relative h-[350px] md:h-[400px] mb-12 flex items-center justify-center">
              {}
              <div className="absolute inset-x-0 top-1/2 -translate-y-1/2 z-30 flex justify-between pointer-events-none px-2 md:-mx-12">
                <Button variant="outline" size="icon" onClick={() => paginate(-1)} className="pointer-events-auto h-12 w-12 rounded-lg bg-white/5 backdrop-blur-xl border-white/10 text-white hover:bg-white/20 transition-all shadow-xl">
                  <ChevronLeft className="h-6 w-6" />
                </Button>
                <Button variant="outline" size="icon" onClick={() => paginate(1)} className="pointer-events-auto h-12 w-12 rounded-lg bg-white/5 backdrop-blur-xl border-white/10 text-white hover:bg-white/20 transition-all shadow-xl">
                  <ChevronRight className="h-6 w-6" />
                </Button>
              </div>

              <AnimatePresence initial={false} custom={direction} mode="wait">
                <motion.div key={currentSlide} custom={direction} variants={variants} initial="enter" animate="center" exit="exit" transition={{
                x: {
                  type: "spring",
                  stiffness: 200,
                  damping: 25
                },
                opacity: {
                  duration: 0.5
                },
                scale: {
                  duration: 0.5
                }
              }} className="absolute inset-0 flex flex-col items-center justify-center bg-black/10 backdrop-blur-sm rounded-2xl p-8 border border-white/5">
                  <h2 className="font-serif text-5xl md:text-7xl lg:text-8xl leading-tight text-white mb-6 drop-shadow-2xl">
                    Journeys curated <br />
                    <span className="italic font-light text-primary">thoughtfully</span>, 
                    <br />
                    to <span className="text-primary">{destinations[currentSlide].name}</span>
                  </h2>
                  <p className="font-serif text-xl md:text-3xl text-white/90 italic max-w-3xl">
                    {destinations[currentSlide].description}
                  </p>
                </motion.div>
              </AnimatePresence>
            </div>

            <p className="font-serif text-lg md:text-xl text-white/90 leading-relaxed mb-12 max-w-2xl mx-auto drop-shadow-md">
              An Innoleaf experience crafting bespoke global escapes and solo adventures for the world's most curious travelers.
            </p>

            <div className="flex flex-wrap justify-center gap-6 mb-12">
              <Button asChild className="bg-primary hover:bg-primary/90 text-primary-foreground px-10 py-7 text-lg rounded-lg shadow-2xl transition-all duration-300">
                <Link to="https://innoleafstays.com/shop/">
                  Explore Destinations
                  <ArrowRight className="ml-2 h-5 w-5" />
                </Link>
              </Button>
            </div>

            {}
            <div className="flex justify-center gap-3 mb-12">
              {destinations.map((_, i) => <Button key={i} data-index={i} variant="ghost" size="sm" onClick={() => {
              const dir = i > currentSlide ? 1 : -1;
              setPage([i, dir]);
              setCurrentSlide(i);
            }} className={`p-0 min-w-0 h-1.5 transition-all duration-300 rounded-lg hover:bg-transparent ${currentSlide === i ? "w-8 bg-primary" : "w-2 bg-white/30 hover:bg-white/50"}`} aria-label={`Go to slide ${i + 1}`} />)}
            </div>

            <div className="flex flex-col items-center gap-4">
              <div className="flex -space-x-3">
                {[1, 2, 3, 4].map(i => <div key={i} data-index={i} className="w-10 h-10 rounded-lg border-2 border-white/20 bg-white/10 backdrop-blur-sm flex items-center justify-center overflow-hidden">
                    <MapPin className="h-5 w-5 text-primary" />
                  </div>)}
              </div>
              <p className="font-sans text-sm text-white/80 tracking-wide">
                Joined by 2,000+ global explorers
              </p>
            </div>
          </motion.div>
        </div>

        {}
        <div className="absolute bottom-10 right-10 z-20 hidden lg:block animate-float">
          <div className="bg-background/90 backdrop-blur-xl p-6 rounded-lg shadow-2xl border border-white/10 max-w-[260px]">
            <div className="flex items-center gap-3 mb-3">
              <div className="p-2 bg-primary/10 rounded-lg">
                <WvcLogo className="h-6 w-auto" />
              </div>
              <span className="font-serif font-medium text-foreground text-lg">Signature Escape</span>
            </div>
            <p className="text-sm text-muted-foreground leading-relaxed">
              Hand-picked private departures.
            </p>
          </div>
        </div>
      </div>
    </section>;
}